/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function Marquee(selector, speed) {
  const parentSelector = document.querySelector(selector);
  if (parentSelector) {
    const clone = parentSelector.innerHTML;
    const firstElement = parentSelector.children[0];
    let i = 0;
    parentSelector.insertAdjacentHTML("beforeend", clone);
    parentSelector.insertAdjacentHTML("beforeend", clone);
    parentSelector.insertAdjacentHTML("beforeend", clone);
    parentSelector.insertAdjacentHTML("beforeend", clone);

    setInterval(function () {
      firstElement.style.marginLeft = `-${i}px`;
      if (i > firstElement.clientWidth) {
        i = 0;
      }
      i = i + speed;
    }, 0);
  }
}

//after window is completed load
//1 class selector for marquee
//2 marquee speed 0.2
window.addEventListener("load", Marquee(".custom-marquee", 0.15));

/**
 * SectionHeroes quantity-upsell support.
 *
 * The app sets `properties[_sh-qu]` (option index) but theme AJAX cart/add
 * may omit `quantity`. Patch fetch/XHR so quantity comes from
 * `sh-quantity-upsell.options[o].quantity`.
 */
(() => {
  const QUANTITY_PROPERTY_NAME = 'properties[_sh-qu]';

  const findQuantityUpsellElement = (anchorEl) => {
    if (anchorEl) {
      const scoped =
        anchorEl.closest('.sh-quantity-upsell-host')?.querySelector('sh-quantity-upsell')
        || anchorEl.closest('form[action*="/cart/add"]')?.querySelector('sh-quantity-upsell')
        || anchorEl.closest('product-form')?.querySelector('sh-quantity-upsell');
      if (scoped) return scoped;
    }
    return document.querySelector('sh-quantity-upsell');
  };

  const getCartQuantityFromShQu = (rawValue, appBlock) => {
    if (!rawValue) return null;
    try {
      const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
      const optionIndex = Number.parseInt(parsed?.o, 10);
      if (!Number.isFinite(optionIndex) || optionIndex < 0) return null;

      const quEl = appBlock || findQuantityUpsellElement(null);
      const opt = quEl?.options?.[optionIndex];
      if (opt != null && opt.quantity != null) {
        const qty = Number(opt.quantity);
        if (Number.isFinite(qty) && qty > 0) return qty;
      }

      return optionIndex + 1;
    } catch (e) {
      // Ignore malformed value.
    }
    return null;
  };

  const readDomShQuQuantity = () => {
    const input = document.querySelector(
      `input[data-sh-qu-property="true"], input[name="${QUANTITY_PROPERTY_NAME}"]`
    );
    if (!input) return null;
    return getCartQuantityFromShQu(input.value, findQuantityUpsellElement(input));
  };

  const isCartAddUrl = (url) => {
    if (!url) return false;
    const urlString = url instanceof Request ? url.url : String(url);
    return urlString.includes('/cart/add') && !urlString.includes('sh_skip=true');
  };

  const transformUrlEncodedBody = (body) => {
    const params = new URLSearchParams(body);
    const shQuValue = params.get(QUANTITY_PROPERTY_NAME);
    const quEl = findQuantityUpsellElement(null);
    const quantity = getCartQuantityFromShQu(shQuValue, quEl) || readDomShQuQuantity();
    if (!quantity) return body;

    params.set('quantity', String(quantity));
    return params.toString();
  };

  const transformFormDataBody = (body) => {
    const shQuValue = body.get(QUANTITY_PROPERTY_NAME);
    const quEl = findQuantityUpsellElement(null);
    const quantity = getCartQuantityFromShQu(shQuValue, quEl) || readDomShQuQuantity();
    if (!quantity) return body;

    body.set('quantity', String(quantity));
    return body;
  };

  const transformJsonBody = (body) => {
    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch (e) {
      return body;
    }

    const domQuantity = readDomShQuQuantity();
    const quEl = findQuantityUpsellElement(null);

    const applyToItem = (item) => {
      if (!item || typeof item !== 'object') return;
      const itemQuantity =
        getCartQuantityFromShQu(item.properties && item.properties['_sh-qu'], quEl) || domQuantity;
      if (itemQuantity) item.quantity = itemQuantity;
    };

    if (Array.isArray(parsed?.items)) {
      if (parsed.items.length > 0) applyToItem(parsed.items[0]);
    } else if (parsed && typeof parsed === 'object') {
      applyToItem(parsed);
    } else {
      return body;
    }

    return JSON.stringify(parsed);
  };

  const transformBody = (body, contentType) => {
    if (!body) return body;
    if (body instanceof FormData) return transformFormDataBody(body);
    if (body instanceof URLSearchParams) {
      const transformed = transformUrlEncodedBody(body.toString());
      return new URLSearchParams(transformed);
    }
    if (typeof body === 'string') {
      const lowerCT = (contentType || '').toLowerCase();
      if (lowerCT.includes('application/json')) return transformJsonBody(body);
      return transformUrlEncodedBody(body);
    }
    return body;
  };

  const originalFetch = window.fetch;
  if (originalFetch && !originalFetch.__shQuPatched) {
    const patchedFetch = function (input, init) {
      const url = input instanceof Request ? input.url : input;
      if (isCartAddUrl(url) && init && init.body && readDomShQuQuantity()) {
        const contentType = init.headers && (init.headers['Content-Type'] || init.headers['content-type']);
        const newBody = transformBody(init.body, contentType);
        if (newBody !== init.body) {
          init = { ...init, body: newBody };
        }
      }
      return originalFetch.call(this, input, init);
    };
    patchedFetch.__shQuPatched = true;
    window.fetch = patchedFetch;
  }

  const OriginalXHR = window.XMLHttpRequest;
  if (OriginalXHR && !OriginalXHR.prototype.__shQuPatched) {
    const originalOpen = OriginalXHR.prototype.open;
    const originalSend = OriginalXHR.prototype.send;
    const originalSetRequestHeader = OriginalXHR.prototype.setRequestHeader;

    OriginalXHR.prototype.open = function (method, url, ...rest) {
      this.__shQuUrl = url;
      this.__shQuRequestHeaders = {};
      return originalOpen.call(this, method, url, ...rest);
    };

    OriginalXHR.prototype.setRequestHeader = function (header, value) {
      if (!this.__shQuRequestHeaders) this.__shQuRequestHeaders = {};
      this.__shQuRequestHeaders[String(header).toLowerCase()] = value;
      return originalSetRequestHeader.call(this, header, value);
    };

    OriginalXHR.prototype.send = function (body) {
      if (isCartAddUrl(this.__shQuUrl) && body && readDomShQuQuantity()) {
        const contentType = this.__shQuRequestHeaders && this.__shQuRequestHeaders['content-type'];
        const newBody = transformBody(body, contentType);
        return originalSend.call(this, newBody);
      }
      return originalSend.call(this, body);
    };

    OriginalXHR.prototype.__shQuPatched = true;
  }
})();
